import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { MainButton } from 'components/atoms/Buttons/MainButton';
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { ReactComponent as TemplateArrow } from './template-arrow.svg';

const labelGlowSweep = keyframes`
  0%, 82% {
    transform: translateX(-160%) skewX(-18deg);
    opacity: 0;
  }

  86% {
    opacity: 0.15;
  }

  92% {
    transform: translateX(160%) skewX(-18deg);
    opacity: 0.45;
  }

  100% {
    transform: translateX(160%) skewX(-18deg);
    opacity: 0;
  }
`;

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? theme.palette.grey[600] : theme.palette.grey[50],
  textAlign: 'center',
}));

const Title = styled(StyledTypography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightBold,
  fontSize: '28px',
  lineHeight: 1.2,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.primary.main,
  textAlign: 'center',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '12px auto 0',
  padding: '4px 12px',
  borderRadius: '999px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  position: 'relative',
  overflow: 'hidden',
  width: 'fit-content',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -2,
    bottom: -2,
    width: '40%',
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0)} 0%, ${alpha(
      theme.palette.primary.main,
      0.42
    )} 50%, ${alpha(theme.palette.primary.main, 0)} 100%)`,
    transform: 'translateX(-160%) skewX(-18deg)',
    animation: `${labelGlowSweep} 10s ease-in-out infinite`,
    pointerEvents: 'none',
  },
}));

const getSubtitleSharedStyles = (theme: Parameters<typeof styled>[0] extends never ? never : any) => ({
  ...theme.typography.overline,
  color: theme.palette.primary.main,
  textAlign: 'center',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '12px auto 0',
  padding: '4px 12px',
  borderRadius: '999px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  position: 'relative',
  overflow: 'hidden',
  width: 'fit-content',
  textDecorationColor: alpha(theme.palette.primary.main, 0.45),
  textUnderlineOffset: '0.28em',
  '&:hover': {
    textDecorationColor: theme.palette.primary.main,
  },
  '&:focus-visible': {
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
    outlineOffset: 2,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -2,
    bottom: -2,
    width: '40%',
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0)} 0%, ${alpha(
      theme.palette.primary.main,
      0.42
    )} 50%, ${alpha(theme.palette.primary.main, 0)} 100%)`,
    transform: 'translateX(-160%) skewX(-18deg)',
    animation: `${labelGlowSweep} 10s ease-in-out infinite`,
    pointerEvents: 'none',
  },
});

const TextContent = styled(Box)(() => ({
  marginTop: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const EmptyStateContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: 'fit-content',
  margin: '0 auto',
}));

const PageContainer = styled(Container)(() => ({
  display: 'flex',
  justifyContent: 'center',
}));

const RoundedBox = styled(Box)(() => ({
  borderRadius: '2px',
}));

const StyledTemplateBox = styled(RoundedBox)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '400px',
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
  margin: '20px 0',
  border: `1px solid ${theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800]}`,
  position: 'relative',
  padding: '15px',
}));

const IllustrationFrame = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-flex',
  justifyContent: 'center',
  paddingRight: '40px',
}));

const LogoTemplate = styled(RoundedBox)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800],
  height: '20px',
}));

const ClickTemplate = styled(RoundedBox)(() => ({
  display: 'flex',
  height: '48px',
  justifyContent: 'flex-end',
  padding: '4px 4px 4px 10px',
}));

const ClickTemplateButton = styled(RoundedBox)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
}));

const ClickTemplateButtonIcon = styled(AddIcon)(() => ({
  fontSize: '24px',
}));

const ContentTemplate = styled(RoundedBox)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[800],
  height: '104px',
}));

const ArrowBox = styled(Box)(() => ({
  position: 'absolute',
  top: '50%',
  right: 0,
  transform: 'translate(50%, -50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
}));

interface Props {
  page: 'studies' | 'cases' | 'slides' | 'procedures' | 'cohorts' | 'accessions';
  title?: string;
  action?: React.ReactNode;
}

const getSubtitle = (page: Props['page']) => {
  if (page === 'studies') {
    return 'Start your first study';
  }

  return `Start adding ${page}`;
};

const getTitle = (page: Props['page']) => {
  if (page === 'studies') {
    return 'No studies have been set up for your lab yet';
  }

  return `There are no ${page} here`;
};

const getSubtitleLinkProps = (page: Props['page'], currentLocation: { pathname: string; search: string }) => {
  if (page !== 'studies') {
    return null;
  }

  return {
    to: {
      pathname: currentLocation.pathname,
      search: currentLocation.search,
    },
    state: { openCreateStudyModal: true },
  };
};

const EmptyPage = ({ page, title, action }: Props) => {
  const location = useLocation();
  const subtitleLinkProps = getSubtitleLinkProps(page, location);

  return (
    <PageContainer className="empty-page max-width-container">
      <EmptyStateContent className="empty-page__content">
        <IllustrationFrame aria-hidden="true">
          <StyledTemplateBox>
            <ArrowBox>
              <TemplateArrow />
            </ArrowBox>
            <Grid container direction="column" spacing={2}>
              <Grid container item direction="row" spacing={2}>
                <Grid item xs={3}>
                  <LogoTemplate />
                </Grid>
              </Grid>
              <Grid container item direction="row" spacing={2}>
                <Grid item xs={12}>
                  <ClickTemplate>
                    {page === 'slides' ? (
                      <MainButton
                        type="button"
                        role="presentation"
                        tabIndex={-1}
                        disableElevation
                        variant="contained"
                        color="primary"
                        sx={{ pointerEvents: 'none' }}
                      >
                        Add Slides
                      </MainButton>
                    ) : (
                      <ClickTemplateButton>
                        <ClickTemplateButtonIcon />
                      </ClickTemplateButton>
                    )}
                  </ClickTemplate>
                </Grid>
              </Grid>
              <Grid container item direction="row" spacing={2}>
                <Grid item xs={12}>
                  <ContentTemplate />
                </Grid>
              </Grid>
            </Grid>
          </StyledTemplateBox>
        </IllustrationFrame>
        <TextContent>
          <Title className="title">{title ?? getTitle(page)}</Title>
          {action !== undefined ? (
            action
          ) : subtitleLinkProps ? (
            <Link
              className="subtitle"
              component={RouterLink}
              sx={(theme) => ({
                ...getSubtitleSharedStyles(theme),
                textDecorationColor: alpha(theme.palette.primary.main, 0.45),
                textUnderlineOffset: '0.28em',
                '&:hover': {
                  textDecorationColor: theme.palette.primary.main,
                },
                '&:focus-visible': {
                  outline: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
                  outlineOffset: 2,
                },
              })}
              to={subtitleLinkProps.to}
              state={subtitleLinkProps.state}
            >
              {getSubtitle(page)}
            </Link>
          ) : (
            <Subtitle className="subtitle">{getSubtitle(page)}</Subtitle>
          )}
        </TextContent>
      </EmptyStateContent>
    </PageContainer>
  );
};

export default EmptyPage;
